import "./styles.css";
import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import createGlobe from "cobe";
import usePartySocket from "partysocket/react";

// Mock types since we don't have the actual shared types
type OutgoingMessage = 
  | { type: "add-marker"; position: { id: string; lat: number; lng: number; } }
  | { type: "remove-marker"; id: string; };

type LegacyRef<T> = React.Ref<T>;

function App() {
  // Your existing PartyKit setup - keeping it exactly as is
  const canvasRef = useRef<HTMLCanvasElement>();
  const [counter, setCounter] = useState(0);
  const positions = useRef<Map<string, { location: [number, number]; size: number; }>>(new Map());

  // New state for the landing page
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState("");
  const [showTypeform, setShowTypeform] = useState(false);

  // Your existing PartyKit socket - keeping it exactly as is
  const socket = usePartySocket({
    room: "default",
    party: "globe",
    onMessage(evt) {
      const message = JSON.parse(evt.data as string) as OutgoingMessage;
      if (message.type === "add-marker") {
        positions.current.set(message.position.id, {
          location: [message.position.lat, message.position.lng],
          size: message.position.id === socket.id ? 0.1 : 0.05,
        });
        setCounter((c) => c + 1);
      } else {
        positions.current.delete(message.id);
        setCounter((c) => c - 1);
      }
    },
  });

  // Your existing globe setup - keeping it exactly as is
  useEffect(() => {
    let phi = 0;
    const globe = createGlobe(canvasRef.current as HTMLCanvasElement, {
      devicePixelRatio: 2,
      width: 400 * 2,
      height: 400 * 2,
      phi: 0,
      theta: 0,
      dark: 1,
      diffuse: 0.8,
      mapSamples: 16000,
      mapBrightness: 6,
      baseColor: [0.3, 0.3, 0.3],
      markerColor: [0.8, 0.1, 0.1],
      glowColor: [0.2, 0.2, 0.2],
      markers: [],
      opacity: 0.7,
      onRender: (state) => {
        state.markers = [...positions.current.values()];
        state.phi = phi;
        phi += 0.01;
      },
    });
    return () => {
      globe.destroy();
    };
  }, []);

  // Handle email submission
  const handleEmailSubmit = () => {
    if (email.trim()) {
      setShowEmailForm(false);
      setShowTypeform(true);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  // Close modals on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowEmailForm(false);
        setShowTypeform(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, []);

  // Load Typeform script when needed
  useEffect(() => {
    if (showTypeform) {
      const script = document.createElement('script');
      script.src = '//embed.typeform.com/next/embed.js';
      script.async = true;
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, [showTypeform]);

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center relative overflow-hidden">
      {/* Ambient background glow */}
      <div className="absolute inset-0 bg-gradient-radial from-gray-900/20 via-black to-black"></div>
      
      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center justify-center px-6 max-w-6xl mx-auto">
        
        {/* Globe container */}
        <div className="relative mb-8 group">
          <div className="absolute inset-0 bg-gradient-radial from-blue-500/10 via-transparent to-transparent blur-3xl group-hover:from-blue-500/20 transition-all duration-1000"></div>
          <canvas
            ref={canvasRef as LegacyRef<HTMLCanvasElement>}
            style={{ width: 400, height: 400, maxWidth: "100%", aspectRatio: 1 }}
            className="relative z-10 drop-shadow-2xl"
          />
        </div>

        {/* Hero text */}
        <div className="text-center mb-12 space-y-6">
          <h1 className="text-6xl md:text-8xl font-bold tracking-tight bg-gradient-to-b from-white via-white to-gray-400 bg-clip-text text-transparent leading-tight">
            The last personal
            <br />
            assistant you will
            <br />
            ever use
          </h1>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-6 items-center">
          {/* Primary CTA */}
          <button
            onClick={() => setShowTypeform(true)}
            onKeyDown={(e) => handleKeyDown(e, () => setShowTypeform(true))}
            style={{
  backgroundColor: 'white', // bg-white
  color: 'black', // text-black
  paddingLeft: '2rem', // px-8 (Tailwind default spacing: 8 = 2rem or 32px)
  paddingRight: '2rem', // px-8
  paddingTop: '1rem', // py-4 (Tailwind default spacing: 4 = 1rem or 16px)
  paddingBottom: '1rem', // py-4
  borderRadius: '9999px', // rounded-full
  fontWeight: '600', // font-semibold
  fontSize: '1.125rem', // text-lg (Tailwind default: 18px or 1.125rem)
  lineHeight: '1.75rem', // text-lg (Tailwind default: 28px or 1.75rem)
  // NOTE: Dynamic styles like hover, focus, transition, transform, and shadow are NOT included here
  // because they cannot be applied using a static style object.
}}
            tabIndex={0}
          >
            Request Access
          </button>
        </div>
      </div>

      {/* Typeform Modal */}
      {showTypeform && (
        <div className="fixed inset-0 bg-black z-50">
          <button
            onClick={() => setShowTypeform(false)}
            onKeyDown={(e) => handleKeyDown(e, () => setShowTypeform(false))}
            className="absolute top-4 right-4 z-10 text-white hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-white/30 rounded p-2"
            tabIndex={0}
            aria-label="Close"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          <div 
            data-tf-live="01JYGC0KX1W4MTS871TBKNMBHE"
            className="w-full h-full"
          ></div>
        </div>
      )}
    </div>
  );
}

// Your existing render setup - keeping it exactly as is  
createRoot(document.getElementById("root")!).render(<App />);
